import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import ListingCard from '../components/ListingCard';

const STATUS_FILTERS = [
  { v: 'all', l: 'All' },
  { v: 'available', l: 'Available' },
  { v: 'deposit_paid', l: 'Deposit Paid' },
  { v: 'sold', l: 'Sold' },
];

export default function SellerProfile() {
  const { username } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => { load(); }, [username]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: p, error: pErr } = await supabase
        .from('profiles')
        .select('id, username, created_at')
        .ilike('username', username)
        .maybeSingle();

      if (pErr) { setError('Database error: ' + pErr.message); setLoading(false); return; }
      if (!p)   { setError('Seller "' + username + '" not found.'); setLoading(false); return; }
      setProfile(p);

      const { data: ls, error: lErr } = await supabase
        .from('listings')
        .select('*, profiles(username), queue_entries(count)')
        .eq('user_id', p.id)
        .eq('hidden', false)
        .order('created_at', { ascending: false });

      if (lErr) { setError('Could not load listings: ' + lErr.message); setLoading(false); return; }
      setListings(ls || []);
    } catch (e) {
      setError('Unexpected error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const visible = listings.filter(l => filter === 'all' || l.status === filter);
  const totalActive = listings.filter(l => l.status === 'available').length;
  const totalSold   = listings.filter(l => l.status === 'sold').length;

  if (loading) return <div className="spinner-wrap"><div className="spinner" /></div>;

  if (error) return (
    <div className="page">
      <div className="alert alert-e" style={{ marginBottom: '1rem' }}>{error}</div>
      <button className="btn btn-g" onClick={() => navigate('/')}>← Browse Listings</button>
    </div>
  );

  const joinedDate = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="page">
      <div style={{ display: 'flex', gap: '.75rem', marginBottom: '1.5rem', alignItems: 'center' }}>
        <button className="btn btn-g btn-sm" onClick={() => navigate(-1)}>← Back</button>
        <button className="btn btn-g btn-sm" onClick={copyLink} style={{ marginLeft: 'auto' }}>
          {copied ? '✓ Copied!' : '🔗 Share Page'}
        </button>
      </div>

      <div style={{
        background: 'linear-gradient(135deg, var(--ink) 0%, #2D1F1A 100%)',
        borderRadius: 'var(--r-lg)', padding: '2rem', marginBottom: '2rem',
        color: 'var(--cream)', display: 'flex', alignItems: 'center',
        gap: '1.5rem', flexWrap: 'wrap',
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%', background: 'var(--terracotta)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.75rem', fontWeight: 900, fontFamily: 'var(--font-d)',
          flexShrink: 0, color: '#fff', border: '3px solid rgba(255,255,255,.15)',
        }}>
          {profile.username.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontFamily: 'var(--font-d)', fontSize: '1.75rem', fontWeight: 900, letterSpacing: '-.5px', marginBottom: '.25rem' }}>
            {profile.username}
          </h1>
          {joinedDate && <div style={{ opacity: .65, fontSize: '.85rem' }}>Member since {joinedDate}</div>}
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          {[{ label: 'Listed', value: listings.length }, { label: 'Available', value: totalActive }, { label: 'Sold', value: totalSold }].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'var(--font-d)', color: 'var(--terracotta)' }}>{s.value}</div>
              <div style={{ fontSize: '.75rem', opacity: .65, textTransform: 'uppercase', letterSpacing: '.5px' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {listings.length > 0 && (
        <div className="filter-bar" style={{ marginBottom: '1.5rem' }}>
          {STATUS_FILTERS.map(f => (
            <button key={f.v} className={`chip ${filter === f.v ? 'on' : ''}`} onClick={() => setFilter(f.v)}>{f.l}</button>
          ))}
        </div>
      )}

      {listings.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">📦</div>
          <h3>No listings yet</h3>
          <p>This seller hasn't posted anything yet.</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="empty">
          <h3>No listings in this category</h3>
          <button className="btn btn-g" style={{ marginTop: '1rem' }} onClick={() => setFilter('all')}>Show all</button>
        </div>
      ) : (
        <>
          <div style={{ color: 'var(--brown)', fontSize: '.875rem', marginBottom: '1rem' }}>
            {visible.length} {visible.length === 1 ? 'listing' : 'listings'}
          </div>
          <div className="grid">
            {visible.map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        </>
      )}
    </div>
  );
}
